/**
 * Expected delivery date math for newly created orders.
 *
 * Per Rafael (2026-06): the order should be promised by the UPPER bound of
 * the working-day range we advertise on the Solution Subtype picker, not the
 * lower bound. So an order containing a carbon insole ("7-10 working days")
 * is promised in 10 working days, not 7. Multi-line orders take the MAX of
 * all included subtypes so the customer sees a single realistic date.
 *
 * Advertised ranges (from SolutionSubtypePicker, upper bound used here):
 *   pa12_insole ............. 5
 *   tpu_insole .............. 5
 *   polypropylene_insole .... 10
 *   carbon_insole ........... 10
 *   eva_insole .............. 10
 *   fibreglass_insole ....... 10
 *   orthopaedic_sandal ...... 14
 *   insoles_refurbishment ... 3
 *
 * Working days = Mon-Fri. Weekends (Sat/Sun) are skipped. No public-holiday
 * handling in v1; admins can still override the date from the tracker.
 *
 * NOTE: A parallel helper (deliveryEstimate.ts) exists in the wizard folder
 * and is still used for catalog (in-stock vs out-of-stock) lines. That file
 * encodes the LOWER bound + stock-aware math for SLA estimation. This file
 * is specifically the UPPER-bound, customer-facing promise date used at
 * order-creation time for custom-made lines.
 */

/** Upper-bound working days per custom solution_subtype. */
const UPPER_BOUND_BY_SUBTYPE: Readonly<Record<string, number>> = {
  pa12_insole: 5,
  tpu_insole: 5,
  polypropylene_insole: 10,
  carbon_insole: 10,
  eva_insole: 10,
  fibreglass_insole: 10,
  orthopaedic_sandal: 14,
  insoles_refurbishment: 3,
};

/** Safe upper bound if we hit an unknown subtype (worst case across the map). */
const FALLBACK_UPPER_BOUND = Math.max(...Object.values(UPPER_BOUND_BY_SUBTYPE));

/** Mon-Fri are business days; Sat (6) and Sun (0) are not. */
function isBusinessDay(d: Date): boolean {
  const day = d.getDay();
  return day !== 0 && day !== 6;
}

/**
 * Add `days` working days (Mon-Fri) to `start`, skipping weekends.
 * Returns a new Date at midnight local time on the resulting business day.
 */
export function addWorkingDays(start: Date, days: number): Date {
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  if (!Number.isFinite(days) || days <= 0) return d;
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (isBusinessDay(d)) added += 1;
  }
  return d;
}

/**
 * Upper-bound working days for a single solution_subtype. Unknown subtypes
 * fall back to the longest published lead time (14) so we never under-promise.
 */
export function workingDaysForSubtype(
  subtype: string | null | undefined,
): number {
  if (!subtype) return FALLBACK_UPPER_BOUND;
  return UPPER_BOUND_BY_SUBTYPE[subtype] ?? FALLBACK_UPPER_BOUND;
}

/** Local-time YYYY-MM-DD (matches the Postgres `date` column shape). */
export function formatDateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Expected delivery Date for an order, given the order date and the list of
 * solution_subtypes on its custom lines. Takes the MAX working-day count
 * across all subtypes so the whole order is promised by one date.
 *
 * Returns null if `subtypes` is empty (caller can decide to fall back to
 * other rules, e.g. catalog stock-based math for non-custom orders).
 */
export function computeExpectedDeliveryDate(
  orderDate: Date,
  subtypes: ReadonlyArray<string | null | undefined>,
): Date | null {
  const valid = subtypes.filter(
    (s): s is string => typeof s === 'string' && s.length > 0,
  );
  if (valid.length === 0) return null;
  const maxDays = valid.reduce(
    (acc, s) => Math.max(acc, workingDaysForSubtype(s)),
    0,
  );
  return addWorkingDays(orderDate, maxDays);
}
